import React, { Component } from "react";
import { FormGroup, FormControl, FormLabel } from "react-bootstrap";
import {
	FiPlus,
	FiCoffee,
	FiLogOut,
	FiRotateCw,
	FiMoreHorizontal,
	FiMapPin,
	FiEye,
	FiEyeOff,
} from "react-icons/fi";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Popover, OverlayTrigger } from "react-bootstrap";
import { withTranslation, Trans } from "react-i18next";
import ContextualHelpBanner from "components/ContextualHelpBanner";
import { isHaddockEnvironment } from "utils/documentUtils";
import { showModal, hideModal } from "actions/modalActions";
import Modal from "components/Modal";
import SubmitButton from "components/SubmitButton";
import { timesService, timeTypesService, authService, employeesService } from "services";
import { format } from "date-fns";
import moment from "moment";
import Logo from "components/Logo";
import {
	trackHaddockEmployeeClockIn,
	resolveHaddockVenueProps,
} from "utils/posthogTracking";
import {
	defaultUbicationId,
	resolveUbicationId,
	toSelectOptions,
	withUbicationId,
} from "components/TimeRegistrations/timeRegistrationUbication";

const MAX_STALE_HOURS = 16;
const NO_UBICATION_DISCLAIMER_MS = 4000;

const defaultClockinMode = () =>
	isHaddockEnvironment() ? "without_ubication" : null;

class Clockin extends Component {
	constructor(props) {
		super(props);

		const companyName = decodeURIComponent(
			this.getCookie("timeRegistrationCompany") || ""
		);
		this.closeModal = this.closeModal.bind(this);
		this.handleClockinClose = this.handleClockinClose.bind(this);
		this.debounceTimer = null;
		this.disclaimerTimer = null;

		this.state = {
			hasToken: !!this.getCookie("timeRegistrationToken"),
			isLoading: false,
			isSearching: false,
			employee_code: "",
			observations: "",
			showObservations: false,
			time_types: [],
			employee: null,
			today_times: [],
			ongoing_time: null,
			clockin_error: "",
			time_response: null,
			time_response_action: null,
			time_response_entry_start: null,
			time_response_worked_minutes: null,
			companyName,
			// Login del terminal (cuando se accede a /clockin sin token)
			terminal_username: "",
			terminal_password: "",
			showTerminalPassword: false,
			terminalLoginLoading: false,
			terminalLoginError: "",
			ubication_id: -1,
			ubication_options: [],
			clockin_mode: defaultClockinMode(),
			showNoUbicationDisclaimer: false,
		};
	}

	closeModal() {
		this.props.hideModal();
	}

	getCookie = (name) => {
		const match = document.cookie.match(
			new RegExp("(^| )" + name + "=([^;]+)")
		);
		return match ? match[2] : null;
	};

	deleteCookie = (name) => {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		const currentDomain = window.location.hostname;
		if (currentDomain.endsWith(".haddock.app")) {
			document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.haddock.app`;
		}
	};

	setCookie = (name, value, maxAgeSeconds) => {
		document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
	};

	trackTerminalClockIn = (clock_action) => {
		const { employee } = this.state;
		const { user } = this.props;
		const { company_id, property_id } = resolveHaddockVenueProps(employee, user);

		trackHaddockEmployeeClockIn(user, {
			employee_id: employee?.id ?? null,
			clock_action,
			source: "web_terminal",
			company_id,
			property_id,
		});
	};

	loadTimeTypes = async (token) => {
		const response = await timeTypesService.getWebTerminal(token);
		if (response.ok) {
			this.setState({ time_types: response.data || [] });
		}
	};

	clearDisclaimerTimer = () => {
		if (this.disclaimerTimer) {
			clearTimeout(this.disclaimerTimer);
			this.disclaimerTimer = null;
		}
	};

	chooseClockinMode = (mode) => {
		this.setState({
			clockin_mode: mode,
			clockin_error: "",
			employee_code: "",
			employee: null,
			ongoing_time: null,
			today_times: [],
			showNoUbicationDisclaimer: false,
			ubication_id: -1,
			ubication_options: [],
		});
	};

	backToModeChoice = () => {
		this.clearDisclaimerTimer();
		this.setState({
			clockin_mode: defaultClockinMode(),
			employee_code: "",
			employee: null,
			ongoing_time: null,
			today_times: [],
			clockin_error: "",
			isSearching: false,
			showNoUbicationDisclaimer: false,
			ubication_id: -1,
			ubication_options: [],
			observations: "",
			showObservations: false,
		});
	};

	handleTerminalLoginChange = (event) => {
		this.setState({ [event.target.id]: event.target.value });
	};

	toggleTerminalPasswordVisibility = () => {
		this.setState((prevState) => ({
			showTerminalPassword: !prevState.showTerminalPassword,
		}));
	};

	handleTerminalLogin = async (event) => {
		event.preventDefault();
		const { terminal_username, terminal_password } = this.state;
		if (!terminal_username || !terminal_password) return;

		this.setState({ terminalLoginLoading: true, terminalLoginError: "" });
		const response = await authService.getWebTerminalToken(
			terminal_username,
			terminal_password
		);

		if (response.ok && response.access_token) {
			const maxAge = 60 * 60 * 24 * 30; // 30 días
			this.setCookie("timeRegistrationToken", response.access_token, maxAge);
			this.setState({
				hasToken: true,
				terminalLoginLoading: false,
				terminal_username: "",
				terminal_password: "",
				showTerminalPassword: false,
				terminalLoginError: "",
			});
			this.loadTimeTypes(response.access_token);
		} else {
			this.setState({
				terminalLoginLoading: false,
				terminalLoginError: this.props.t("clockin.loginError"),
			});
		}
	};

	async componentDidMount() {
		const token = this.getCookie("timeRegistrationToken");
		if (!token) return;
		this.loadTimeTypes(token);
	}

	componentWillUnmount() {
		clearTimeout(this.debounceTimer);
		this.clearDisclaimerTimer();
	}

	handleClockinClose() {
		const { t } = this.props;
		this.props.showModal(
			{
				open: true,
				title: t("clockin.closeTerminal"),
				style: { width: "350px" },
				message: t("clockin.closeTerminalConfirm"),
				deleteText: t("clockin.closeTerminal"),
				deleteAction: () => this.confirmClockinClose(),
				closeModal: this.closeModal,
			},
			"delete"
		);
	}

	confirmClockinClose = async () => {
		this.deleteCookie("timeRegistrationToken");
		this.deleteCookie("timeRegistrationCompany");
		this.props.history.push("/login");
		this.closeModal();
	};

	handleCodeChange = (event) => {
		const value = event.target.value;
		this.setState({
			employee_code: value,
			employee: null,
			ongoing_time: null,
			today_times: [],
			clockin_error: "",
			isSearching: value.length > 0,
			showNoUbicationDisclaimer: false,
			ubication_options: [],
			ubication_id: -1,
		});
		clearTimeout(this.debounceTimer);
		if (value.length > 0) {
			this.debounceTimer = setTimeout(() => {
				this.lookupEmployee(value);
			}, 2000);
		}
	};

	handleCodeBlur = () => {
		clearTimeout(this.debounceTimer);
		if (this.state.employee_code.length > 0) {
			this.lookupEmployee(this.state.employee_code);
		}
	};

	lookupEmployee = async (code) => {
		const token = this.getCookie("timeRegistrationToken");
		if (!token) return;

		const response = await timesService.getWebTerminal(code, token);
		if (response.ok) {
			const times = Array.isArray(response.data) ? response.data : [];
			const employee =
				response.employee || times[0]?.employee || { code };

			// Same stale logic as Dashboard_Clockin: ignore open fichajes older than MAX_STALE_HOURS
			const openTime = times.find((t) => !t.end);
			let ongoing = null;
			if (openTime) {
				const elapsedHours = moment().diff(
					moment.utc(openTime.start),
					"hours",
					true
				);
				if (elapsedHours <= MAX_STALE_HOURS) {
					ongoing = openTime;
				} else {
					console.log(
						"[Clockin] Fichaje abierto ignorado por antigüedad:",
						openTime
					);
				}
			}

			let resolvedUbicationOptions = [];
			let locationsLoaded = false;
			const wantsUbication = this.state.clockin_mode === "with_ubication";

			if (wantsUbication && !ongoing) {
				const ubicationsResponse =
					await employeesService.getUbicationsForWebTerminal(code, token);
				if (ubicationsResponse.ok) {
					locationsLoaded = true;
					resolvedUbicationOptions = toSelectOptions(ubicationsResponse, []);
				}
			}

			const ubication_id = ongoing
				? defaultUbicationId(
						resolvedUbicationOptions,
						resolveUbicationId(ongoing)
					)
				: defaultUbicationId(resolvedUbicationOptions, -1);

			const shouldWaitForUbication =
				!ongoing && wantsUbication && resolvedUbicationOptions.length > 0;
			const shouldShowDisclaimer =
				!ongoing &&
				wantsUbication &&
				locationsLoaded &&
				resolvedUbicationOptions.length === 0;

			this.setState(
				{
					employee,
					today_times: times,
					ongoing_time: ongoing,
					observations: ongoing?.observations || "",
					showObservations: !!ongoing?.observations,
					clockin_error: "",
					isSearching: false,
					ubication_options: resolvedUbicationOptions,
					ubication_id,
					showNoUbicationDisclaimer: shouldShowDisclaimer,
				},
				() => {
					if (ongoing || shouldWaitForUbication) return;
					if (shouldShowDisclaimer) {
						this.scheduleDisclaimerClockIn();
						return;
					}
					this.handleClockIn();
				}
			);
		} else {
			this.setState({
				employee: null,
				today_times: [],
				ongoing_time: null,
				clockin_error: this.props.t("clockin.codeNotFound"),
				isSearching: false,
				showNoUbicationDisclaimer: false,
			});
		}
	};

	scheduleDisclaimerClockIn = () => {
		this.clearDisclaimerTimer();
		this.disclaimerTimer = setTimeout(() => {
			this.setState({ showNoUbicationDisclaimer: false });
			this.handleClockIn();
		}, NO_UBICATION_DISCLAIMER_MS);
	};

	handleChange = (event) => {
		const { id, value } = event.target;
		this.setState({
			[id]: id === "ubication_id" ? parseInt(value, 10) : value,
		});
	};

	handleShowObservations = () => {
		this.setState({ showObservations: true });
	};

	// Sum closed work_time:true fichajes for today, optionally adding the just-closed ongoing session
	calcWorkedMinutes = (includeOngoing = false) => {
		const { today_times, ongoing_time } = this.state;
		const today = moment().local().format("YYYY-MM-DD");
		let total = 0;
		for (const t of today_times) {
			if (!t.end) continue;
			if (t.type?.work_time === false) continue;
			if (moment.utc(t.start).local().format("YYYY-MM-DD") !== today) continue;
			total += moment(t.end).diff(moment(t.start), "minutes");
		}
		if (
			includeOngoing &&
			ongoing_time?.start &&
			ongoing_time?.type?.work_time !== false
		) {
			total += moment().diff(moment.utc(ongoing_time.start), "minutes");
		}
		return total;
	};

	// Close ongoing and open a new one with targetTypeId (mirrors switchTimeType in Dashboard_Clockin)
	switchTimeType = async (targetTypeId, action) => {
		const { employee_code, ongoing_time, observations, ubication_id } =
			this.state;
		const token = this.getCookie("timeRegistrationToken");
		if (!token || !ongoing_time?.id) return;

		this.setState({ isLoading: true, clockin_error: "" });
		try {
			const closeTime = withUbicationId(
				{
					id: ongoing_time.id,
					employee_code,
					start: moment.utc(ongoing_time.start).format(),
					end: moment().utc().format(),
					type_id: ongoing_time.type.id,
					observations,
					approved: false,
					start_source: ongoing_time.start_source || "WEB_TERMINAL",
					end_source: "WEB_TERMINAL",
					start_origin: ongoing_time.start_origin || "WebTerminal",
					end_origin: "WebTerminal",
				},
				ongoing_time,
				{ echo: true }
			);
			const closeResponse = await timesService.updateWebTerminal(
				ongoing_time.id,
				closeTime,
				token
			);
			if (!closeResponse.ok) {
				this.setState({
					clockin_error:
						closeResponse.errors?.[0]?.description ||
						this.props.t("clockin.errorChangeType"),
				});
				return;
			}

			const newTime = withUbicationId(
				{
					employee_code,
					start: moment().utc().format(),
					type_id: targetTypeId,
					observations,
					approved: false,
					start_source: "WEB_TERMINAL",
					end_source: "WEB_TERMINAL",
					start_origin: "WebTerminal",
					end_origin: "WebTerminal",
				},
				resolveUbicationId(ongoing_time) ?? ubication_id ?? -1,
				{ echo: false }
			);
			const startResponse = await timesService.addWebTerminal(newTime, token);
			if (startResponse.ok) {
				// For pausa: include the just-closed work session. For fin_descanso: ongoing was a pause, don't include it.
				const workedMinutes = this.calcWorkedMinutes(action === "pausa");
				this.trackTerminalClockIn(action);
				this.setState({
					time_response: startResponse,
					time_response_action: action,
					time_response_entry_start: ongoing_time.start,
					time_response_worked_minutes: workedMinutes,
					clockin_error: "",
				});
				setTimeout(() => this.resetForm(), 5000);
			} else {
				this.setState({
					clockin_error:
						startResponse.errors?.[0]?.description ||
						this.props.t("clockin.errorStartTime"),
				});
			}
		} finally {
			this.setState({ isLoading: false });
		}
	};

	handleClockIn = async () => {
		const { employee_code, ongoing_time, observations, ubication_id, isLoading } =
			this.state;
		const token = this.getCookie("timeRegistrationToken");
		if (!token || !employee_code || isLoading) return;

		this.setState({ isLoading: true, clockin_error: "" });
		try {
			let response;

			if (ongoing_time?.id) {
				// Fichar salida — mirror of Dashboard_Clockin handleClockIn close path
				const time = withUbicationId(
					{
						id: ongoing_time.id,
						employee_code,
						start: moment.utc(ongoing_time.start).format(),
						end: moment().utc().format(),
						type_id: ongoing_time.type.id,
						observations,
						approved: false,
						start_source: ongoing_time.start_source || "WEB_TERMINAL",
						end_source: "WEB_TERMINAL",
						start_origin: ongoing_time.start_origin || "WebTerminal",
						end_origin: "WebTerminal",
					},
					ongoing_time,
					{ echo: true }
				);
				response = await timesService.updateWebTerminal(
					ongoing_time.id,
					time,
					token
				);

				if (response.ok) {
					this.trackTerminalClockIn("salida");
					this.setState({
						time_response: response,
						time_response_action: "salida",
						time_response_entry_start: null,
						time_response_worked_minutes: this.calcWorkedMinutes(true),
						clockin_error: "",
					});
					setTimeout(() => this.resetForm(), 5000);
				} else {
					// Keep ongoing state on error, same as Dashboard_Clockin
					this.setState({
						clockin_error:
							response.errors?.[0]?.description ||
							this.props.t("clockin.errorClockOut"),
						ongoing_time: ongoing_time,
					});
				}
			} else {
				// Fichar entrada
				const time = withUbicationId(
					{
						employee_code,
						observations,
						start: moment().utc().format(),
						approved: false,
						start_source: "WEB_TERMINAL",
						end_source: "WEB_TERMINAL",
						start_origin: "WebTerminal",
						end_origin: "WebTerminal",
					},
					ubication_id ?? -1,
					{ echo: false }
				);
				response = await timesService.addWebTerminal(time, token);

				if (response.ok) {
					this.trackTerminalClockIn("entrada");
					this.setState({
						time_response: response,
						time_response_action: "entrada",
						time_response_entry_start: null,
						time_response_worked_minutes: null,
						clockin_error: "",
					});
					setTimeout(() => this.resetForm(), 5000);
				} else {
					this.setState({
						clockin_error:
							response.errors?.[0]?.description ||
							this.props.t("clockin.errorClockIn"),
					});
				}
			}
		} catch (error) {
			this.setState({ clockin_error: this.props.t("clockin.errorUnexpected") });
		} finally {
			this.setState({ isLoading: false });
		}
	};

	resetForm = () => {
		this.clearDisclaimerTimer();
		this.setState({
			time_response: null,
			time_response_action: null,
			time_response_entry_start: null,
			time_response_worked_minutes: null,
			employee_code: "",
			observations: "",
			showObservations: false,
			employee: null,
			today_times: [],
			ongoing_time: null,
			clockin_error: "",
			ubication_id: -1,
			ubication_options: [],
			clockin_mode: defaultClockinMode(),
			showNoUbicationDisclaimer: false,
		});
	};

	resetEmployee = () => {
		this.clearDisclaimerTimer();
		this.setState({
			employee_code: "",
			employee: null,
			today_times: [],
			ongoing_time: null,
			clockin_error: "",
			showNoUbicationDisclaimer: false,
			ubication_id: -1,
			ubication_options: [],
		});
	};

	resolveUbicationLabel = (timeRegistration) => {
		if (!timeRegistration) return null;

		const fromResponse = timeRegistration.ubication?.name;
		if (fromResponse) return fromResponse;

		const ubicationId =
			resolveUbicationId(timeRegistration) ??
			(this.state.ubication_id > 0 ? this.state.ubication_id : null);
		if (ubicationId == null) return null;

		const match = this.state.ubication_options.find(
			(opt) => opt.id === ubicationId
		);
		return match?.name ?? null;
	};

	TimeRegistrationMessage({
		timeRegistration,
		action,
		entryStart,
		workedMinutes,
	}) {
		if (!timeRegistration) return null;
		const { t } = this.props;
		const { employee, start, end } = timeRegistration;
		const fmt = (d) => format(new Date(d), "HH:mm");
		const fmtMinutes = (min) => {
			const h = Math.floor(min / 60);
			const m = min % 60;
			return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
		};

		const actionLabels = {
			entrada: t("clockin.actionEntrada"),
			salida: t("clockin.actionSalida"),
			pausa: t("clockin.actionPausa"),
			fin_descanso: t("clockin.actionFinDescanso"),
		};
		const label = actionLabels[action] || actionLabels.entrada;
		const displayTime = action === "salida" && end ? fmt(end) : fmt(start);
		const ubicationName = this.resolveUbicationLabel(timeRegistration);

		return (
			<div className="text-center clockin-result">
				<p className="clockin-result-name">
					{employee.name} {employee.surname}
				</p>

				<hr className="clockin-result-divider" />

				<p className="clockin-result-label">{label}</p>
				<p className="clockin-result-time">{displayTime}</p>

				{ubicationName && (
					<p className="clockin-result-secondary">
						{t("clockin.ubicationAt")}{" "}
						<strong>{ubicationName}</strong>
					</p>
				)}

				{action === "pausa" && entryStart && (
					<p className="clockin-result-secondary">
						{t("clockin.entryTime")} <strong>{fmt(entryStart)}</strong>
					</p>
				)}

				{workedMinutes != null && (
					<>
						<hr className="clockin-result-divider" />
						<p className="clockin-result-secondary">
							{t("clockin.workedToday")}{" "}
							<strong>{fmtMinutes(workedMinutes)}</strong>
						</p>
					</>
				)}
			</div>
		);
	}

	renderTerminalLogin() {
		const { t } = this.props;
		const {
			terminal_username,
			terminal_password,
			showTerminalPassword,
			terminalLoginLoading,
			terminalLoginError,
		} = this.state;

		return (
			<div className="Auth">
				<div className="clockin-container">
					<div className="login-container">
						<Logo className="logo" type="logo" />

						<h3 className="login-title">{t("clockin.loginTitle")}</h3>
						<p className="hello">{t("clockin.loginIntro")}</p>

						<ContextualHelpBanner className="clockin-login-info">
							{isHaddockEnvironment() ? (
								<Trans
									i18nKey="clockin.loginInfoHaddock"
									components={{
										settingsPath: <strong />,
										appLink: (
											<Link
												to="/login"
												className="contextual-help-banner__bold-link"
											/>
										),
									}}
								/>
							) : (
								<Trans
									i18nKey="clockin.loginInfoPlain"
									components={{
										settingsPath: <strong />,
										moreInfoLink: (
											<a
												href="https://help.plain.ninja/es/articles/15521784-como-activar-las-credenciales-del-terminal-de-fichajes"
												target="_blank"
												rel="noopener noreferrer"
												className="contextual-help-banner__bold-link"
											/>
										),
										appLink: (
											<Link
												to="/login"
												className="contextual-help-banner__bold-link"
											/>
										),
									}}
								/>
							)}
						</ContextualHelpBanner>

						<form className="login-form" onSubmit={this.handleTerminalLogin}>
							<FormGroup
								className="form-group login-form-group"
								controlId="terminal_username"
							>
								<FormLabel>{t("clockin.loginEmail")}</FormLabel>
								<FormControl
									autoFocus
									type="email"
									value={terminal_username}
									onChange={this.handleTerminalLoginChange}
									placeholder={t("clockin.loginEmailPlaceholder")}
									autoComplete="username"
								/>
							</FormGroup>
							<FormGroup
								className="form-group login-form-group"
								controlId="terminal_password"
							>
								<FormLabel>{t("clockin.loginPassword")}</FormLabel>
								<div className="password-input-wrapper">
									<FormControl
										type={showTerminalPassword ? "text" : "password"}
										value={terminal_password}
										onChange={this.handleTerminalLoginChange}
										placeholder="••••••••"
										autoComplete="current-password"
									/>
									<button
										type="button"
										className="password-toggle-btn"
										onClick={this.toggleTerminalPasswordVisibility}
										aria-label={
											showTerminalPassword
												? t("login.hidePassword")
												: t("login.showPassword")
										}
									>
										{showTerminalPassword ? (
											<FiEyeOff size={18} />
										) : (
											<FiEye size={18} />
										)}
									</button>
								</div>
							</FormGroup>

							{terminalLoginError && (
								<div className="alert alert-danger">{terminalLoginError}</div>
							)}

							<SubmitButton
								block
								disabled={!terminal_username || !terminal_password}
								type="submit"
								isLoading={terminalLoginLoading}
								text={t("clockin.loginSubmit")}
								loadingText={t("clockin.loginSubmitting")}
							/>
						</form>
					</div>
				</div>
			</div>
		);
	}

	renderModeChoice() {
		const { t } = this.props;

		return (
			<>
				<div className="clockin-question">
					<p className="clockin-question-title">{t("clockin.modeQuestion")}</p>
					<p className="clockin-question-hint">{t("clockin.modeHint")}</p>
				</div>

				<div className="clockin-actions">
					<button
						type="button"
						onClick={() => this.chooseClockinMode("without_ubication")}
						className="clockin-action-card clockin-action-card--without-ubication"
					>
						<span className="clockin-action-card-icon" aria-hidden="true">
							<svg
								viewBox="0 0 24 24"
								width="1em"
								height="1em"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
								<circle cx="12" cy="10" r="3" />
								<line x1="4" y1="4" x2="20" y2="20" />
							</svg>
						</span>
						<span className="clockin-action-card-title">
							{t("clockin.modeWithoutTitle")}
						</span>
						<span className="clockin-action-card-desc">
							{t("clockin.modeWithoutDesc")}
						</span>
					</button>

					<button
						type="button"
						onClick={() => this.chooseClockinMode("with_ubication")}
						className="clockin-action-card clockin-action-card--with-ubication"
					>
						<FiMapPin className="clockin-action-card-icon" />
						<span className="clockin-action-card-title">
							{t("clockin.modeWithTitle")}
						</span>
						<span className="clockin-action-card-desc">
							{t("clockin.modeWithDesc")}
						</span>
					</button>
				</div>
			</>
		);
	}

	renderEmployeeIdentity() {
		const { t } = this.props;
		const { employee } = this.state;
		if (!employee?.name) return null;

		return (
			<div className="clockin-identity">
				<p className="clockin-employee-name">
					{employee.name} {employee.surname}
				</p>
				<button
					type="button"
					className="clockin-change-employee"
					onClick={this.resetEmployee}
				>
					{t("clockin.notYou")}
				</button>
			</div>
		);
	}

	render() {
		const { t } = this.props;
		const {
			hasToken,
			time_response,
			time_response_action,
			time_response_entry_start,
			time_response_worked_minutes,
			companyName,
			ongoing_time,
			employee,
			employee_code,
			clockin_error,
			isLoading,
			isSearching,
			observations,
			showObservations,
			time_types,
			ubication_id,
			ubication_options,
			clockin_mode,
			showNoUbicationDisclaimer,
		} = this.state;

		if (!hasToken) {
			return this.renderTerminalLogin();
		}

		// Mirror of Dashboard_Clockin pause detection
		const pauseType = time_types.find(
			(t) =>
				t.name.toLowerCase().includes("pausa") ||
				t.name.toLowerCase().includes("descanso")
		);
		const defaultWorkType = time_types.find(
			(t) => t.is_default || t.name.toLowerCase() === "trabajo"
		);
		const isPaused =
			pauseType && ongoing_time?.type && ongoing_time.type.id === pauseType.id;

		const popover = (
			<Popover id="popoverMoreOptions" className="auth-popover-footer">
				<ul className="list-unstyled">
					<li className="more-option">
						<button className="button-link" onClick={this.handleClockinClose}>
							{t("clockin.closeTerminalBtn")}
						</button>
					</li>
				</ul>
			</Popover>
		);

		return (
			<>
				<div className="Auth">
					<div className="clockin-container">
						<div className="login-container">
							<Logo className="logo" type="logo" />

							{!time_response && !employee && (
								<h3 className="login-title">{t("clockin.title")}</h3>
							)}
							{!time_response && !employee && companyName && (
								<h4 className="login-subtitle">{companyName}</h4>
							)}

							<form className="login-form" onSubmit={(e) => e.preventDefault()}>
								{!time_response && (
									<>
										{!clockin_mode &&
											!isHaddockEnvironment() &&
											this.renderModeChoice()}

										{clockin_mode && !employee && !ongoing_time ? (
											<>
												<p className="hello">{t("clockin.intro")}</p>

												<FormGroup
													controlId="employee_code"
													className="form-group login-form-group"
												>
													<FormControl
														autoFocus
														type="text"
														value={employee_code}
														onChange={this.handleCodeChange}
														onBlur={this.handleCodeBlur}
														placeholder={t("clockin.codePlaceholder")}
														autoComplete="off"
													/>
												</FormGroup>

												{!isHaddockEnvironment() && (
													<p className="text-center">
														<button
															type="button"
															className="clockin-change-employee"
															onClick={this.backToModeChoice}
														>
															{t("clockin.backToMode")}
														</button>
													</p>
												)}
											</>
										) : null}

										{showNoUbicationDisclaimer && (
											<>
												{this.renderEmployeeIdentity()}
												<div className="clockin-disclaimer" role="status">
													<p className="clockin-disclaimer-title">
														{t("clockin.noUbicationDisclaimerTitle")}
													</p>
													<p className="clockin-disclaimer-text">
														{t("clockin.noUbicationDisclaimer")}
													</p>
												</div>
											</>
										)}

										{employee &&
											!ongoing_time &&
											!showNoUbicationDisclaimer &&
											ubication_options.length > 0 && (
												<>
													{this.renderEmployeeIdentity()}
													<FormGroup
														className="form-group login-form-group"
														controlId="ubication_id"
													>
														<FormLabel>
															{t("clockin.ubicationLabel")}
														</FormLabel>
														<FormControl
															as="select"
															id="ubication_id"
															value={ubication_id ?? -1}
															onChange={this.handleChange}
														>
															<option value="-1">
																{t("times.selectUbication")}
															</option>
															{ubication_options.map((item) => (
																<option key={item.id} value={item.id}>
																	{item.name}
																</option>
															))}
														</FormControl>
													</FormGroup>
													<SubmitButton
														block
														type="button"
														onClick={this.handleClockIn}
														disabled={isLoading}
														isLoading={isLoading}
														text={t("clockin.accessBtn")}
														loadingText={t("clockin.clockingIn")}
													/>
												</>
											)}

										{isSearching && (
											<p className="clockin-searching">
												{t("clockin.searching")}
											</p>
										)}

										{isLoading &&
											!isSearching &&
											!showNoUbicationDisclaimer &&
											ubication_options.length === 0 && (
											<p className="clockin-searching">
												{t("clockin.clockingIn")}
											</p>
										)}

										{!isSearching && !isLoading && clockin_error && (
											<div className="alert alert-danger">{clockin_error}</div>
										)}

										{ongoing_time ? (
											<>
												<p className="hello text-center">
													{t("clockin.ongoingSince")}{" "}
													<strong>
														{moment
															.utc(ongoing_time.start)
															.local()
															.format("HH:mm")}
													</strong>{" "}
													({ongoing_time.type?.name})
												</p>
												{this.renderEmployeeIdentity()}
												<hr className="clockin-question-divider" />

												<div className="clockin-question">
													<p className="clockin-question-title">
														{t("clockin.whatNow")}
													</p>
													<p className="clockin-question-hint">
														{t("clockin.whatNowHint")}
													</p>
												</div>

												<div className="clockin-actions">
													{isPaused ? (
														<button
															type="button"
															onClick={() =>
																this.switchTimeType(
																	defaultWorkType?.id ||
																		time_types.find(
																			(t) => t.id !== pauseType?.id
																		)?.id,
																	"fin_descanso"
																)
															}
															className="clockin-action-card clockin-action-card--pause"
															disabled={isLoading}
														>
															<FiRotateCw className="clockin-action-card-icon" />
															<span className="clockin-action-card-title">
																{t("clockin.resume")}
															</span>
															<span className="clockin-action-card-desc">
																{t("clockin.resumeDesc")}
															</span>
															<span className="clockin-action-card-note">
																{t("clockin.resumeNote")}
															</span>
														</button>
													) : (
														<button
															type="button"
															onClick={() =>
																pauseType &&
																this.switchTimeType(pauseType.id, "pausa")
															}
															className="clockin-action-card clockin-action-card--pause"
															disabled={isLoading || !pauseType}
														>
															<FiCoffee className="clockin-action-card-icon" />
															<span className="clockin-action-card-title">
																{t("clockin.pause")}
															</span>
															<span className="clockin-action-card-desc">
																{t("clockin.pauseDesc")}
															</span>
															<span className="clockin-action-card-note">
																{t("clockin.pauseNote")}
															</span>
														</button>
													)}

													<button
														type="button"
														onClick={this.handleClockIn}
														className="clockin-action-card clockin-action-card--stop"
														disabled={isLoading}
													>
														<FiLogOut className="clockin-action-card-icon" />
														<span className="clockin-action-card-title">
															{t("clockin.stop")}
														</span>
														<span className="clockin-action-card-desc">
															{t("clockin.stopDesc")}
														</span>
														<span className="clockin-action-card-note">
															{t("clockin.stopNote")}
														</span>
													</button>
												</div>

												{!showObservations && (
													<button
														type="button"
														onClick={this.handleShowObservations}
														className="link-icon link-icon--center"
													>
														<FiPlus className="icon" />
														{t("clockin.addObservations")}
													</button>
												)}

												{showObservations && (
													<FormGroup
														className="form-group"
														controlId="observations"
													>
														<FormLabel>{t("clockin.observations")}</FormLabel>
														<FormControl
															as="textarea"
															id="observations"
															value={observations}
															onChange={this.handleChange}
															placeholder={t("clockin.observationsPlaceholder")}
														/>
													</FormGroup>
												)}
											</>
										) : null}
									</>
								)}

								{time_response &&
									this.TimeRegistrationMessage({
										timeRegistration: time_response,
										action: time_response_action,
										entryStart: time_response_entry_start,
										workedMinutes: time_response_worked_minutes,
									})}
							</form>
						</div>
						<div className="more-options-menu">
							<OverlayTrigger overlay={popover} trigger="click">
								<span className="menu-trigger">
									<FiMoreHorizontal className="icon-opciones" />{" "}
									{t("clockin.moreOptions")}
								</span>
							</OverlayTrigger>
						</div>
					</div>
				</div>

				<Modal hideModal={this.props.hideModal} />
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	return reducers.authReducer;
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withTranslation()(Clockin));
